import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { SerializedError } from '@reduxjs/toolkit';
import {
  ApiErrorStatusCode,
  getAsyncStatusFromRtkMutationStates,
  isQueryError,
  isResponseWithData,
  QueryError,
} from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { AsyncStatus } from 'lib-shared-types-api';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import snakeCase from 'lodash/snakeCase';
import { useCallback, useEffect, useMemo } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useSet } from 'react-use';
import { v4 as uuid } from 'uuid';

import {
  CreateAttributeApiArg,
  CustomFieldAttribute,
  customFieldsApi,
  CustomFieldSelectAttribute,
  UpdateAttributeApiArg,
} from 'apis/customFields';
import { useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { treeFilter } from 'utils/tree';

import {
  DescriptionFormField,
  IdentifierFormField,
  LabelFormField,
  RequiredFormField,
  TypeFormField,
} from './components/fields';
import {
  ToggleVisibilityFormField,
  VisibilityFormField,
} from './components/fields/visibility';
import styles from './customAttributeModal.module.scss';
import { AttributeFormFieldValues } from './customAttributeModalForm.types';
import { attributeTypeFormAttributes } from './utils/attributeTypeFormAttributes';
import { patchSelectAttribute } from './utils/patchSelectAttribute';

const formId = uuid();

/** Represents the modal for creating or editing a custom attribute. */
export const CustomAttributeModal = createAppModal(
  { type: 'settings:customAttribute' },
  ({ isOpen, modal, hide }): JSX.Element | null => {
    const notify = useNotifications();
    const { mode = 'create', entityId } = modal.arg;

    const [conflictingIdentifiers, { add: addConflictingIdentifier }] =
      useSet<string>(new Set([]));

    const [createAttribute, createAttributeResult] =
      customFieldsApi.useCreateAttributeMutation();
    const [updateAttribute, updateAttributeResult] =
      customFieldsApi.useUpdateAttributeMutation();

    const form = useForm<AttributeFormFieldValues>({
      mode: 'all',
      defaultValues:
        modal.arg.mode === 'edit'
          ? // Note that we pass the whole custom field attribute object which
            // includes fields that we can't or won't use during editing, e.g.
            // `id`, `organization_id`. This means that the object RHF
            // returns on successful submit will contain those fields, which we
            // just pass to the endpoint and let it pick and choose and what it
            // requires. This makes consumption and implementation a lot simpler
            // and flexible in terms of adding new custom field attribute types.
            modal.arg.attribute
          : {
              type: 'text',
              data_type: attributeTypeFormAttributes.text.data_type,
              label: '',
              identifier: '',
              description: '',
              placeholder: '',
              visibility_condition: null,
            },
    });

    const { formState } = form;

    // While we don't use `dirtyFields` directly, it seems to affect when
    // `formState` recomputes. Here we subscribe to it by destructuring it.
    // See formState rules: https://react-hook-form.com/api/useform/formstate#rules
    const { isValid, isSubmitting, dirtyFields: _dirtyFields } = formState;

    /**
     * Whether the form is truly dirty.
     *
     * In some cases we have something like
     * {@link CustomFieldSelectAttributeOption.options options field} where we
     * inject an empty option to make creation of new options quicker. Such an
     * addition is seen by React Hook Form as a dirty operation and therefore
     * computes the dirty state not as we would like to have it. To circumvent
     * this, we compute our own dirty state.
     */
    const isTrueDirty = useMemo(() => {
      const currentValues = form.getValues();

      if (
        modal.arg.mode === 'edit' &&
        modal.arg.attribute.type === 'select' &&
        currentValues.type === 'select'
      ) {
        return !isEqual(
          patchSelectAttribute(modal.arg.attribute),
          patchSelectAttribute(currentValues as CustomFieldSelectAttribute),
        );
      }

      return formState.isDirty;
    }, [form, formState, modal]);

    // TODO: [TEAMS-13693] remove casting after updating to RHF v8
    const type = useWatch({ control: form.control as never, name: 'type' });
    const visibilityConditionValue = useWatch({
      control: form.control,
      name: 'visibility_condition',
    });

    const asyncStatus = useMemo<AsyncStatus>(() => {
      const status: readonly AsyncStatus[] = [
        getAsyncStatusFromRtkMutationStates(createAttributeResult),
        getAsyncStatusFromRtkMutationStates(updateAttributeResult),
      ];

      if (status.some((asyncStatus) => asyncStatus === 'pending')) {
        return 'pending';
      }

      if (status.some((asyncStatus) => asyncStatus === 'rejected')) {
        return 'rejected';
      }

      if (status.some((asyncStatus) => asyncStatus === 'fulfilled')) {
        return 'fulfilled';
      }

      return 'uninitialized';
    }, [createAttributeResult, updateAttributeResult]);

    const canSubmit = isTrueDirty && isValid && !isSubmitting;

    /** Handles the successful create. */
    const createSuccess = useCallback(
      (arg: CreateAttributeApiArg) => {
        hide?.();

        notify({
          title: 'Custom attribute created',
          text: 'Successfully created new custom attribute',
          time: 5000,
          type: 'success',
          icon: 'done',
          status: 'success',
        });

        const { attribute, entityId } = arg;
        const { description, identifier, label, type } = attribute;
        const isDescriptionEmpty = !description;
        const isIdentifierInferred = snakeCase(label) === identifier;

        ampli.settingsCustomAttributeCreated({
          entityId,
          type,
          isDescriptionEmpty,
          isIdentifierInferred,
        });
      },
      [hide, notify],
    );

    /** Handles the erroneous create. */
    const createError = useCallback(
      (response: { error: QueryError | SerializedError }) => {
        if (isQueryError(response.error)) {
          if (response.error.status === ApiErrorStatusCode.Conflict) {
            // TODO: [TEAMS-13693] remove casting after updating to RHF v8
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            addConflictingIdentifier((form.getValues as any)('identifier'));
          } else {
            notify({
              title: 'Could not create custom attribute',
              text: 'An error occurred while creating new custom attribute',
              type: 'error',
              icon: 'exclamation',
              status: 'danger',
            });
          }
        }

        createAttributeResult.reset();
      },
      [addConflictingIdentifier, createAttributeResult, form, notify],
    );

    /** Handles creating a custom attribute. */
    const create = useCallback(
      async (arg: CreateAttributeApiArg) => {
        const response = await createAttribute(arg);

        if (isResponseWithData(response)) {
          createSuccess(arg);
        } else {
          createError(response);
        }
      },
      [createAttribute, createSuccess, createError],
    );

    /** Handles the successful update. */
    const updateSuccess = useCallback(
      (arg: UpdateAttributeApiArg) => {
        hide?.();

        notify({
          title: 'Custom attribute updated',
          text: 'Successfully updated a custom attribute',
          time: 5000,
          type: 'success',
          icon: 'done',
          status: 'success',
        });

        const { attributeId, entityId, patch } = arg;
        const isDescriptionUpdated =
          patch.before.description !== patch.after.description;
        const isDescriptionRemoved =
          isDescriptionUpdated && !patch.after.description;
        const isLabelUpdated = patch.before.label !== patch.after.label;

        ampli.settingsCustomAttributeUpdated({
          attributeId,
          entityId,
          type: patch.before.type,
          isDescriptionRemoved,
          isDescriptionUpdated,
          isLabelUpdated,
        });
      },
      [hide, notify],
    );

    /** Handles the erroneous update. */
    const updateError = useCallback(() => {
      updateAttributeResult.reset();

      notify({
        title: 'Could not update custom attribute',
        text: 'An error occurred while updating a custom attribute',
        type: 'error',
        icon: 'exclamation',
        status: 'danger',
      });
    }, [notify, updateAttributeResult]);

    /** Handles updating of a custom attribute. */
    const update = useCallback(
      async (arg: UpdateAttributeApiArg) => {
        const response = await updateAttribute(arg);

        if (isResponseWithData(response)) {
          updateSuccess(arg);
        } else {
          updateError();
        }
      },
      [updateAttribute, updateSuccess, updateError],
    );

    /** Handles valid form submit. */
    const handleValidSubmit = useCallback<
      SubmitHandler<AttributeFormFieldValues>
    >(
      async (attribute) => {
        const { mode, entityId } = modal.arg;

        const patchedAttribute = Object.assign(
          attribute,
          attribute.type === 'select'
            ? {
                options: treeFilter(
                  attribute.options,
                  ({ label, value }) => !!label && !!value,
                  'options',
                ),
              }
            : {},
        );

        if (mode === 'create') {
          create({
            entityId,
            attribute: patchedAttribute as CreateAttributeApiArg['attribute'],
          });
        } else {
          update({
            entityId,
            attributeId: modal.arg.attribute.id,
            patch: {
              before: modal.arg.attribute,
              after: patchedAttribute as CustomFieldAttribute,
            },
          });
        }
      },
      [modal, create, update],
    );

    // Handles setting conflicting names in create mode.
    useEffect(() => {
      if (modal.arg.mode === 'create') {
        modal.arg.attributes?.forEach(({ identifier }) =>
          addConflictingIdentifier(identifier),
        );
      }
    }, [addConflictingIdentifier, modal]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={hide}
        className={styles.root}
        restoreFocus
      >
        <ModalHead
          title={
            mode === 'create'
              ? `Create new ${entityId} attribute`
              : `Edit ${entityId} attribute`
          }
          onClose={hide}
        />

        <ModalBody>
          <FormProvider {...form}>
            <form
              id={formId}
              noValidate
              tabIndex={-1}
              onSubmit={form.handleSubmit(handleValidSubmit)}
            >
              <FormGrid>
                {mode === 'create' && (
                  <FormField>
                    <TypeFormField />
                  </FormField>
                )}

                <FormField span={3}>
                  <LabelFormField />
                </FormField>

                <FormField span={3}>
                  <IdentifierFormField
                    mode={mode}
                    conflictingIdentifiers={conflictingIdentifiers}
                  />
                </FormField>

                <FormField>
                  <DescriptionFormField />
                </FormField>

                {type &&
                  attributeTypeFormAttributes[type].fields.map(
                    (AttributeTypeFormField, index) => (
                      <FormField key={index}>
                        <AttributeTypeFormField />
                      </FormField>
                    ),
                  )}

                {entityId === 'conversation' && (
                  <FormField>
                    <RequiredFormField entityId={modal.arg.entityId} />
                  </FormField>
                )}

                {entityId === 'conversation' && (
                  <>
                    <FormField>
                      <ToggleVisibilityFormField
                        entityId={modal.arg.entityId}
                      />
                    </FormField>
                    {visibilityConditionValue !== null && (
                      <FormField>
                        <VisibilityFormField />
                      </FormField>
                    )}
                  </>
                )}
              </FormGrid>
            </form>
          </FormProvider>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName={mode === 'create' ? 'Create' : 'Update'}
          confirmOnClick={noop}
          confirmProps={{
            type: 'submit',
            disabled: !canSubmit,
            isLoading: asyncStatus === 'pending',
            loadingIndicator: mode === 'create' ? 'Creating...' : 'Updating...',
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
